import { Component, Input, OnInit } from '@angular/core';
import { FornecedoresService } from '../fornecedores.service';
import { NotificationService } from '../../../core/notification.service';
import { TestemunhoService } from '../../testemunhos/testemunho.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  standalone: false,
  selector: 'app-fornecedor-detail',
  template: `
    <div *ngIf="fornecedor" class="bg-white p-4 rounded shadow">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div class="lg:col-span-2">
          <h1 class="text-3xl font-serif mb-2">{{ fornecedor.nome }}</h1>
          <div class="text-sm text-muted mb-4">{{ fornecedor.cidade }} • ★ {{ fornecedor.rating }}</div>
          <p class="text-base">{{ fornecedor.descricao }}</p>

          <div class="mt-6">
            <h3 class="font-semibold mb-2">Galeria</h3>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div *ngFor="let m of fornecedor.medias; let i = index" class="overflow-hidden rounded cursor-pointer" (click)="openLightbox(i)">
                <img [src]="m.url" [alt]="m.filename" class="w-full h-40 object-cover" loading="lazy"/>
              </div>
            </div>
          </div>
        </div>

        <aside class="bg-surface p-3 rounded">
          <h4 class="font-medium">Contato</h4>
          <div class="text-sm mt-2">Telefone: {{ fornecedor.telefone || '—' }}</div>
          <div class="text-sm">Email: {{ fornecedor.email || '—' }}</div>

          <form [formGroup]="contactForm" (ngSubmit)="sendContact()" class="mt-4 space-y-3">
            <div>
              <label class="block text-sm font-medium">Nome *</label>
              <input formControlName="nome" class="w-full border border-border rounded px-3 py-2" />
              <div *ngIf="contactForm.controls['nome'].invalid && contactForm.controls['nome'].touched" class="text-sm text-danger">Nome é obrigatório</div>
            </div>

            <div>
              <label class="block text-sm font-medium">Email *</label>
              <input formControlName="email" class="w-full border border-border rounded px-3 py-2" />
              <div *ngIf="contactForm.controls['email'].invalid && contactForm.controls['email'].touched" class="text-sm text-danger">Email inválido</div>
            </div>

            <div>
              <label class="block text-sm font-medium">Telefone</label>
              <input formControlName="telefone" class="w-full border border-border rounded px-3 py-2" />
            </div>

            <div>
              <label class="block text-sm font-medium">Mensagem *</label>
              <textarea formControlName="mensagem" rows="4" class="w-full border border-border rounded px-3 py-2"></textarea>
              <div *ngIf="contactForm.controls['mensagem'].invalid && contactForm.controls['mensagem'].touched" class="text-sm text-danger">Mensagem é obrigatória</div>
            </div>

            <div>
              <button type="submit" [disabled]="contactForm.invalid || sending" class="bg-primary text-white px-4 py-2 rounded">Enviar</button>
              <span *ngIf="success" class="text-sm text-success ml-3">Mensagem enviada com sucesso.</span>
            </div>
          </form>
        </aside>
      </div>

      <!-- Testimonials Section (moved outside lightbox) -->
      <div class="mt-10 lg:col-span-3" *ngIf="fornecedor">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xl font-semibold">Avaliações e Testemunhos</h3>
            <span class="text-xs px-2 py-1 rounded bg-surface border border-border">{{ displayedTestemunhos.length }} registro(s)</span>
          </div>
          <div *ngIf="displayedTestemunhos.length === 0" class="text-sm text-muted mb-4">Nenhum testemunho até o momento. Seja o primeiro a compartilhar sua experiência.</div>
          <div class="space-y-5">
            <div *ngFor="let t of displayedTestemunhos" class="relative group rounded-lg border border-border bg-white shadow-sm hover:shadow-md transition-shadow">
              <div class="px-4 py-3">
                <div class="flex items-start justify-between gap-3">
                  <div class="flex-1">
                    <div class="font-medium text-sm md:text-base flex items-center gap-2">
                      <span class="inline-block">{{ t.nome }}</span>
                    </div>
                    <div class="text-xs text-muted mt-0.5">{{ t.createdAt | date:'dd/MM/yyyy HH:mm' }}</div>
                  </div>
                </div>
                <div class="mt-3 text-sm leading-relaxed whitespace-pre-line">
                  <ng-container *ngIf="!isExpanded(t.id); else fullText">
                    {{ truncateDescricao(t.descricao) }}<span *ngIf="shouldTruncate(t.descricao)" class="text-muted">...</span>
                  </ng-container>
                  <ng-template #fullText>{{ t.descricao }}</ng-template>
                </div>
                <div class="mt-2 flex items-center gap-3">
                  <button *ngIf="shouldTruncate(t.descricao)" (click)="toggleExpand(t.id)" class="text-xs text-primary hover:underline">
                    {{ isExpanded(t.id) ? 'Ver menos' : 'Ver mais' }}
                  </button>
                </div>
              </div>
              <div class="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-primary/70 to-primary rounded-t"></div>
            </div>
          </div>
          <div class="mt-6 flex flex-wrap gap-3">
            <button class="bg-primary text-white px-4 py-2 rounded shadow hover:shadow-md transition" (click)="openTestemunhoModal()">Deixar Testemunho</button>
            <button *ngIf="canLoadMore" class="px-4 py-2 border rounded hover:bg-surface" (click)="loadMoreTestemunhos()">Carregar mais</button>
          </div>
      </div>

      <!-- Lightbox modal -->
      <div *ngIf="lightboxOpen" class="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        <div class="max-w-3xl w-full">
          <img [src]="currentImage?.url" class="w-full h-auto rounded" />
          <div class="mt-3 flex justify-between">
            <button (click)="prevImage()" class="px-3 py-1 bg-white rounded">Anterior</button>
            <button (click)="closeLightbox()" class="px-3 py-1 bg-white rounded">Fechar</button>
            <button (click)="nextImage()" class="px-3 py-1 bg-white rounded">Próxima</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Novo Testemunho -->
    <div *ngIf="testemunhoModalOpen" class="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div class="bg-white w-full max-w-lg rounded shadow p-5 space-y-4">
        <h3 class="text-lg font-semibold">Novo Testemunho</h3>
        <form [formGroup]="testemunhoForm" (ngSubmit)="submitTestemunho()" class="space-y-3">
          <div>
            <label class="block text-sm font-medium">Nome *</label>
            <input formControlName="nome" class="w-full border border-border rounded px-3 py-2" />
            <div *ngIf="testemunhoForm.controls['nome'].invalid && testemunhoForm.controls['nome'].touched" class="text-xs text-danger">Nome obrigatório (3-200)</div>
          </div>
          <div>
            <label class="block text-sm font-medium">Depoimento *</label>
            <textarea formControlName="descricao" rows="4" class="w-full border border-border rounded px-3 py-2"></textarea>
            <div *ngIf="testemunhoForm.controls['descricao'].invalid && testemunhoForm.controls['descricao'].touched" class="text-xs text-danger">Descrição obrigatória (10-2000)</div>
            <div class="text-right text-xs text-muted">{{ testemunhoForm.controls['descricao'].value?.length || 0 }}/2000</div>
          </div>
          <div class="flex gap-2 justify-end">
            <button type="button" class="px-3 py-2 border rounded" (click)="closeTestemunhoModal()">Cancelar</button>
            <button type="submit" [disabled]="testemunhoForm.invalid || savingTestemunho" class="bg-primary text-white px-4 py-2 rounded">Salvar</button>
          </div>
        </form>
      </div>
    </div>
  `
})
export class FornecedorDetailComponent implements OnInit {
  @Input() slug?: string;
  fornecedor: any;

  contactForm: FormGroup;
  sending = false;
  success = false;

  // lightbox state
  lightboxOpen = false;
  currentIndex = 0;

  displayedTestemunhos: any[] = [];
  testemunhoModalOpen = false;
  testemunhoForm!: FormGroup;
  savingTestemunho = false;
  testemunhoPage = 1;
  testemunhoPageSize = 10;
  testemunhoTotal = 0;

  constructor(private svc: FornecedoresService,
              private fb: FormBuilder,
              private notification: NotificationService,
              private testemunhoSvc: TestemunhoService) {
    this.contactForm = this.fb.group({
      nome: ['', [Validators.required, Validators.maxLength(200)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(200)]],
      telefone: ['', [Validators.maxLength(50)]],
      mensagem: ['', [Validators.required, Validators.maxLength(2000)]]
    });
  }

  ngOnInit(): void {
    if (this.slug) this.load();
  }

  load() {
    this.svc.getBySlug(this.slug!).subscribe(res => {
      this.fornecedor = res;
      // If backend didn't include medias, fetch them explicitly
      if (!this.fornecedor?.medias || this.fornecedor.medias.length === 0) {
        this.loadMedias();
      }
      this.initTestemunhosFromFornecedor();
    });
  }

  loadMedias() {
    if (!this.fornecedor || !this.fornecedor.id) {
      this.fornecedor = this.fornecedor || {};
      this.fornecedor.medias = this.fornecedor.medias || [];
      return;
    }
    this.svc.getMedia(this.fornecedor.id).subscribe((res: any) => {
      this.fornecedor.medias = (res && res.data) ? res.data : (Array.isArray(res) ? res : (res.medias || []));
    }, err => {
      console.error('[FornecedorDetail] failed to load medias', err);
      this.fornecedor.medias = this.fornecedor.medias || [];
    });
  }

  sendContact() {
    if (this.contactForm.invalid || !this.fornecedor) return;
    this.sending = true;
    this.svc.contactFornecedor(this.fornecedor.id, this.contactForm.value).subscribe({
      next: (res: any) => {
        this.sending = false;
        this.success = true;
        this.contactForm.reset();
        setTimeout(() => this.success = false, 5000);
        this.notification.showSuccess('Mensagem enviada com sucesso.');
      },
      error: (err: any) => {
        this.sending = false;
        // handle validation problem mapping (ErrorInterceptor already maps)
        if (err && err.validation && err.validation.fieldErrors) {
          const fields = err.validation.fieldErrors;
          Object.keys(fields).forEach(fieldKey => {
            const control = this.contactForm.get(fieldKey.toLowerCase()) || this.contactForm.get(fieldKey);
            const messages = fields[fieldKey];
            if (control) control.setErrors({ server: messages.join(' ') });
          });
        } else {
          console.error(err);
        }
        this.notification.showError('Erro ao enviar mensagem.');
      }
    });
  }

  // Lightbox controls
  openLightbox(index: number) {
    this.currentIndex = index;
    this.lightboxOpen = true;
  }

  closeLightbox() { this.lightboxOpen = false; }

  prevImage() {
    if (!this.fornecedor?.medias?.length) return;
    this.currentIndex = (this.currentIndex - 1 + this.fornecedor.medias.length) % this.fornecedor.medias.length;
  }

  nextImage() {
    if (!this.fornecedor?.medias?.length) return;
    this.currentIndex = (this.currentIndex + 1) % this.fornecedor.medias.length;
  }

  get currentImage() {
    return this.fornecedor?.medias?.[this.currentIndex];
  }

  // Testemunhos
  initTestemunhosFromFornecedor() {
    const arr = this.fornecedor?.testemunhos || [];
    this.displayedTestemunhos = [...arr];
    this.testemunhoTotal = arr.length;
    // prepare form
    this.testemunhoForm = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(200)]],
      descricao: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(2000)]]
    });
  }

  openTestemunhoModal() { 
    this.testemunhoModalOpen = true; 
  }
  closeTestemunhoModal() { 
    this.testemunhoModalOpen = false; 
    this.testemunhoForm.reset(); 
  }

  submitTestemunho() {
    if (this.testemunhoForm.invalid || !this.fornecedor?.id) return;
    this.savingTestemunho = true;
    const payload = { ...this.testemunhoForm.value, fornecedorId: this.fornecedor.id };
    
    this.testemunhoSvc.create(payload).subscribe({
      next: (t: any) => {
        this.savingTestemunho = false;
        this.displayedTestemunhos = [t, ...this.displayedTestemunhos];
        if (this.fornecedor.testemunhos) this.fornecedor.testemunhos.unshift(t); else this.fornecedor.testemunhos = [t];
        this.notification.showSuccess('Testemunho salvo com sucesso');
        this.closeTestemunhoModal();
      },
      error: (err: any) => {
        this.savingTestemunho = false;
        console.error('[FornecedorDetail] erro ao criar testemunho', err);
        this.handleFormErrors(err);
        this.notification.showError('Erro ao salvar testemunho');
      }
    });
  }

  get canLoadMore() {
    return this.fornecedor?.id && this.displayedTestemunhos.length < this.testemunhoTotal;
  }

  loadMoreTestemunhos() {
    if (!this.fornecedor?.id) return;
    this.testemunhoPage += 1;
    this.testemunhoSvc.listByFornecedor(this.fornecedor.id, this.testemunhoPage, this.testemunhoPageSize).subscribe({
      next: (res: any) => {
        const newItems = res?.data || [];
        // avoid duplicates if backend always returns page slice
        const existingIds = new Set(this.displayedTestemunhos.map(t => t.id));
        newItems.forEach((t: any) => { if (!existingIds.has(t.id)) this.displayedTestemunhos.push(t); });
        this.testemunhoTotal = res?.meta?.total || this.testemunhoTotal;
      },
      error: (err: any) => {
        console.error('[FornecedorDetail] falha ao carregar mais testemunhos', err);
        this.notification.showError('Erro ao carregar mais testemunhos');
      }
    });
  }

  // UI helpers for truncation/expansion
  private expandedIds = new Set<string>();
  shouldTruncate(descricao: string): boolean { return !!descricao && descricao.length > 220; }
  truncateDescricao(descricao: string): string { return this.shouldTruncate(descricao) ? descricao.slice(0, 220) : descricao; }
  toggleExpand(id: string) { if (this.expandedIds.has(id)) this.expandedIds.delete(id); else this.expandedIds.add(id); }
  isExpanded(id: string): boolean { return this.expandedIds.has(id); }

  private handleFormErrors(err: any) {
    if (err && err.validation && err.validation.fieldErrors) {
      const fields = err.validation.fieldErrors;
      Object.keys(fields).forEach(fieldKey => {
        const ctrl = this.testemunhoForm.get(fieldKey.toLowerCase()) || this.testemunhoForm.get(fieldKey);
        if (ctrl) ctrl.setErrors({ server: fields[fieldKey].join(' ') });
      });
    }
  }
}
